import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { UnnamedSegment } from '@fullstory/proto/fs/services/dx-objects';
import { alphanumericUID } from '@fullstory/util/uid';
import { AppDispatch } from '../../types';
import { UnnamedSegmentActions } from './actions';
import { hashForUnnamedSegment } from './util';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { fullstorySelectors, unnamedSegmentsSelectors } from '../../selectors';

const { getOrgId } = fullstorySelectors;
const { makeUnnamedSegmentByIdHash } = unnamedSegmentsSelectors;

export const useUnnamedSegment = (
  segmentDefinition: SegmentDefinition,
  disabled: UnnamedSegment.Disabled,
  preventSave: boolean = false,
  name: string = '',
  description: string = '',
) => {
  const dispatch = useDispatch<AppDispatch>();
  const hash = hashForUnnamedSegment(segmentDefinition, disabled, name, description);
  // anytime the hash changes, we get a new selector for cached hash -> id mapping as well as a generated id if new
  const { getUnnamedSegmentById, idForNew } = useMemo(
    () => ({
      getUnnamedSegmentById: makeUnnamedSegmentByIdHash(hash),
      idForNew: alphanumericUID(),
    }),
    [hash],
  );
  const idByHash = useSelector(getUnnamedSegmentById);
  // always prefer the mapped idByHash if exists, otherwise we use the newly generated id to persist the unnamed segment
  const unnamedSegmentId = idByHash || idForNew;
  const orgId = useSelector(getOrgId);

  const unnamedSegment = useMemo(
    (): UnnamedSegment => ({
      id: unnamedSegmentId,
      definition: segmentDefinition,
      disabled,
      name,
      description,
    }),
    [disabled, name, description, segmentDefinition, unnamedSegmentId],
  );

  useEffect(() => {
    if (!idByHash && !preventSave) {
      dispatch(UnnamedSegmentActions.saveUnnamedSegment({ orgId, unnamedSegment }));
    }
  }, [
    unnamedSegmentId,
    dispatch,
    disabled,
    orgId,
    segmentDefinition,
    unnamedSegment,
    idByHash,
    preventSave,
  ]);

  return { unnamedSegment, unnamedSegmentId, unsaved: !idByHash };
};
